import { useState } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";

const PINK = "#84BBCD";   // Ocean Mist — buttons & accents
const DARK = "#9FCBD9";   // Serene Sky — page background
const CARD = "#FFFFFF";   // Crystal Clear — card background
const BORDER = "#C6E7F2"; // Glacial Breeze — borders
const MUTED = "#5A9BB0";  // muted teal text
const WHITE = "#FFFFFF";
const TEAL = "#6AAEC4";   // Survey 2 accent

const globalStyle = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #9FCBD9; }
  ::selection { background: #9FCBD955; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #84BBCD; border-radius: 2px; }
`;

// ── Shared Components ──────────────────────────────────────────────────────

function ProgressBar({ current, total, color = PINK }) {
  return (
    <div style={{ width: "100%", height: 3, background: "#B6DDEA", borderRadius: 2, marginBottom: 32 }}>
      <div style={{
        height: "100%", width: `${(current / total) * 100}%`,
        background: color, borderRadius: 2,
        transition: "width 0.5s cubic-bezier(0.4,0,0.2,1)"
      }} />
    </div>
  );
}

function QuestionCounter({ current, total, color = PINK }) {
  return (
    <div style={{ color: MUTED, fontSize: 12, fontFamily: "'DM Sans'", fontWeight: 500, marginBottom: 8, letterSpacing: "0.12em" }}>
      <span style={{ color }}>{current}</span> / {total}
    </div>
  );
}

function SectionBadge({ label, color = PINK }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: `${color}18`, border: `1px solid ${color}40`,
      borderRadius: 20, padding: "4px 14px", marginBottom: 16,
      color, fontFamily: "'DM Sans'", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em"
    }}>
      <div style={{ width: 5, height: 5, borderRadius: "50%", background: color }} />
      {label}
    </div>
  );
}

function RadioOption({ label, selected, onSelect, color = PINK }) {
  return (
    <button onClick={onSelect} style={{
      display: "flex", alignItems: "center", gap: 14,
      width: "100%", background: selected ? `${color}15` : "transparent",
      border: `1.5px solid ${selected ? color : BORDER}`,
      borderRadius: 10, padding: "13px 16px", cursor: "pointer",
      transition: "all 0.2s", marginBottom: 8, textAlign: "left"
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
        border: `2px solid ${selected ? color : MUTED}`,
        background: selected ? color : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s"
      }}>
        {selected && <div style={{ width: 6, height: 6, borderRadius: "50%", background: WHITE }} />}
      </div>
      <span style={{ color: selected ? color : "#2A5F70", fontFamily: "'DM Sans'", fontSize: 15, fontWeight: selected ? 500 : 400 }}>
        {label}
      </span>
    </button>
  );
}

function CheckOption({ label, selected, onToggle, color = PINK }) {
  return (
    <button onClick={onToggle} style={{
      display: "flex", alignItems: "center", gap: 14,
      width: "100%", background: selected ? `${color}15` : "transparent",
      border: `1.5px solid ${selected ? color : BORDER}`,
      borderRadius: 10, padding: "13px 16px", cursor: "pointer",
      transition: "all 0.2s", marginBottom: 8, textAlign: "left"
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: 4, flexShrink: 0,
        border: `2px solid ${selected ? color : MUTED}`,
        background: selected ? color : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s"
      }}>
        {selected && <span style={{ color: WHITE, fontSize: 11, fontWeight: 700 }}>✓</span>}
      </div>
      <span style={{ color: selected ? color : "#2A5F70", fontFamily: "'DM Sans'", fontSize: 15, fontWeight: selected ? 500 : 400 }}>
        {label}
      </span>
    </button>
  );
}

function ScaleInput({ value, onChange, low, high, max = 5, color = PINK }) {
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        {Array.from({ length: max }, (_, i) => i + 1).map(n => (
          <button key={n} onClick={() => onChange(n)} style={{
            flex: 1, height: 52, borderRadius: 10, cursor: "pointer",
            border: `1.5px solid ${value === n ? color : BORDER}`,
            background: value === n ? color : "transparent",
            color: value === n ? WHITE : MUTED,
            fontFamily: "'DM Sans'", fontSize: 16, fontWeight: 700,
            transition: "all 0.2s"
          }}>{n}</button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ color: MUTED, fontSize: 12, fontFamily: "'DM Sans'" }}>{low}</span>
        <span style={{ color: MUTED, fontSize: 12, fontFamily: "'DM Sans'" }}>{high}</span>
      </div>
    </div>
  );
}

function TextInput({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: "100%", background: "#F8FCFE", border: `1.5px solid ${BORDER}`,
        borderRadius: 10, padding: "14px 16px", color: "#2A5F70",
        fontFamily: "'DM Sans'", fontSize: 15, resize: "none", outline: "none",
        transition: "border-color 0.2s", lineHeight: 1.6
      }}
      onFocus={e => e.target.style.borderColor = PINK}
      onBlur={e => e.target.style.borderColor = BORDER}
    />
  );
}

function EmailInput({ value, onChange, color = PINK }) {
  return (
    <input
      type="email" value={value} onChange={e => onChange(e.target.value)}
      placeholder="your@email.com"
      style={{
        width: "100%", background: "#F8FCFE", border: `1.5px solid ${BORDER}`,
        borderRadius: 10, padding: "14px 16px", color: "#2A5F70",
        fontFamily: "'DM Sans'", fontSize: 15, outline: "none",
        transition: "border-color 0.2s"
      }}
      onFocus={e => e.target.style.borderColor = color}
      onBlur={e => e.target.style.borderColor = BORDER}
    />
  );
}

function TextInputSingle({ value, onChange, placeholder, color = PINK }) {
  return (
    <input
      type="text" value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%", background: "#F8FCFE", border: `1.5px solid ${BORDER}`,
        borderRadius: 10, padding: "14px 16px", color: "#2A5F70",
        fontFamily: "'DM Sans'", fontSize: 15, outline: "none",
        transition: "border-color 0.2s"
      }}
      onFocus={e => e.target.style.borderColor = color}
      onBlur={e => e.target.style.borderColor = BORDER}
    />
  );
}

function NavBtn({ onClick, label, primary = false, color = PINK, disabled = false }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: "13px 28px", borderRadius: 10, cursor: disabled ? "not-allowed" : "pointer",
      border: primary ? "none" : `1.5px solid ${BORDER}`,
      background: primary ? color : "transparent",
      color: primary ? WHITE : MUTED,
      fontFamily: "'DM Sans'", fontSize: 14, fontWeight: 600,
      opacity: disabled ? 0.4 : 1, transition: "all 0.2s",
      boxShadow: primary && !disabled ? `0 4px 16px ${color}40` : "none"
    }}>{label}</button>
  );
}

function WelcomeScreen({ title, subtitle, cta, badge, onStart, color = PINK, time, questionCount }) {
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{
        display: "inline-block", background: `${color}20`, border: `1px solid ${color}50`,
        borderRadius: 20, padding: "5px 16px", marginBottom: 28,
        color, fontFamily: "'DM Sans'", fontSize: 12, fontWeight: 600, letterSpacing: "0.1em"
      }}>{badge}</div>
      <h1 style={{
        fontFamily: "'Playfair Display'", fontSize: "clamp(28px,5vw,46px)",
        fontWeight: 900, color: "#2A5F70", lineHeight: 1.2, marginBottom: 20,
        whiteSpace: "pre-line"
      }}>{title}</h1>
      <p style={{
        color: MUTED, fontFamily: "'DM Sans'", fontSize: 15,
        lineHeight: 1.7, marginBottom: 12, maxWidth: 400, margin: "0 auto 12px"
      }}>{subtitle}</p>
      {time && (
        <p style={{ color: `${color}`, fontFamily: "'DM Sans'", fontSize: 13, fontWeight: 500, marginBottom: 36 }}>
          ⏱ {time} · {questionCount} questions
        </p>
      )}
      <button onClick={onStart} style={{
        background: color, color: WHITE, border: "none",
        borderRadius: 12, padding: "16px 40px", cursor: "pointer",
        fontFamily: "'DM Sans'", fontSize: 16, fontWeight: 700,
        boxShadow: `0 8px 32px ${color}50`, transition: "all 0.2s",
        marginTop: 12
      }}>{cta} →</button>
    </div>
  );
}

function ThankYouScreen({ title, body, tag, color = PINK }) {
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{ fontSize: 52, marginBottom: 24 }}>💗</div>
      <h2 style={{
        fontFamily: "'Playfair Display'", fontSize: "clamp(26px,4vw,36px)", fontWeight: 900,
        color: "#2A5F70", marginBottom: 16, lineHeight: 1.25
      }}>{title}</h2>
      <p style={{ color: MUTED, fontFamily: "'DM Sans'", fontSize: 15, lineHeight: 1.7, maxWidth: 380, margin: "0 auto 28px" }}>
        {body}
      </p>
      {tag && (
        <div style={{
          display: "inline-block", background: `${color}18`, border: `1px solid ${color}40`,
          color, fontFamily: "'DM Sans'", fontSize: 13, fontWeight: 500,
          padding: "10px 20px", borderRadius: 99, letterSpacing: "0.05em"
        }}>{tag}</div>
      )}
    </div>
  );
}

function HintBadge({ text, color = PINK }) {
  return (
    <div style={{
      background: `${color}12`, border: `1px solid ${color}30`,
      borderRadius: 8, padding: "7px 12px", marginBottom: 16,
      color, fontFamily: "'DM Sans'", fontSize: 12, fontWeight: 500
    }}>{text}</div>
  );
}

// ── SURVEY 1: Event Feedback ───────────────────────────────────────────────

const s1Questions = [
  {
    id: "event_type", type: "radio", required: true,
    section: "About the Event",
    question: "Which event did you attend?",
    options: ["Pilates", "Yoga", "HIIT", "Strength Training", "Wellness Workshop", "Other"]
  },
  {
    id: "overall_rating", type: "scale", required: true,
    section: "About the Event",
    question: "Overall, how would you rate this event?",
    low: "Not worth it", high: "Loved every minute", max: 5
  },
  {
    id: "what_liked", type: "check", required: true,
    section: "What Worked",
    question: "What did you love about the event?",
    hint: "Select all that apply",
    options: [
      "The workout content",
      "The instructor / coach energy",
      "The vibe in the room",
      "Connecting with other women",
      "The structure and flow",
      "The location or venue"
    ]
  },
  {
    id: "loved_more", type: "text", required: false,
    section: "What Worked",
    question: "Anything else you loved? Tell us in your own words.",
    placeholder: "The more detail the better..."
  },
  {
    id: "what_didnt_work", type: "check", required: false,
    section: "What to Improve",
    question: "What didn't quite work for you?",
    hint: "Honest feedback only makes the next one better",
    options: [
      "Workout was too easy",
      "Workout was too advanced",
      "It ran too long",
      "It ran too short",
      "Needed more community / social time",
      "The location wasn't convenient",
      "Not enough variety in the exercises",
      "Nothing — it was great"
    ]
  },
  {
    id: "one_change", type: "text", required: false,
    section: "What to Improve",
    question: "What's one thing we should change or add next time?",
    placeholder: "We read every single response..."
  },
  {
    id: "format_next", type: "check", required: true,
    section: "Future Events",
    question: "What formats do you want more of?",
    hint: "Select all that apply",
    options: [
      "Pilates",
      "Yoga",
      "HIIT",
      "Strength Training",
      "Mindset & Wellness",
      "Mix it up — keep it varied"
    ]
  },
  {
    id: "frequency", type: "radio", required: true,
    section: "Future Events",
    question: "How often do you want to attend Body by MJ events?",
    options: ["Weekly", "2x a month", "Monthly", "Quarterly"]
  },
  {
    id: "virtual", type: "radio", required: true,
    section: "Future Events",
    question: "If there was a virtual option, would you join from home?",
    options: [
      "Yes — I'd tune in from home",
      "Sometimes — depends on the event",
      "No — I prefer in person only"
    ]
  },
  {
    id: "event_time", type: "radio", required: true,
    section: "Future Events",
    question: "What time works best for you?",
    options: [
      "Early morning (6–9am)",
      "Mid-morning (9am–12pm)",
      "Afternoon (12–4pm)",
      "Evening (5–8pm)",
      "Weekend mornings"
    ]
  },
  {
    id: "price_point", type: "radio", required: true,
    section: "Future Events",
    question: "What would you comfortably pay for a Body by MJ event?",
    options: [
      "Free only",
      "$10–$20 per event",
      "$20–$35 per event",
      "$35–$50 per event",
      "$50+ for a premium experience"
    ]
  },
  {
    id: "likelihood_return", type: "radio", required: true,
    section: "Your Connection",
    question: "How likely are you to come back to another Body by MJ event?",
    options: [
      "Definitely — already looking for the next one",
      "Very likely",
      "Possibly — depends on the topic",
      "Unlikely"
    ]
  },
  {
    id: "refer", type: "radio", required: true,
    section: "Your Connection",
    question: "Would you recommend a Body by MJ event to another woman in your life?",
    options: [
      "Already have — I'm spreading the word",
      "Yes — I'd send her the link today",
      "Maybe — if it fits what she's into",
      "Probably not"
    ]
  },
  {
    id: "refer_words", type: "text", required: false,
    section: "Your Connection",
    question: "How would you describe Body by MJ to a friend?",
    placeholder: "In your own words — what would you tell her?"
  },
  {
    id: "discovery", type: "radio", required: true,
    section: "Your Connection",
    question: "How did you first find out about us?",
    options: [
      "Instagram",
      "A friend or word of mouth",
      "TikTok",
      "Sweatpals app",
      "Google",
      "Email / newsletter",
      "In person",
      "Other"
    ]
  },
  {
    id: "email", type: "email", required: false,
    section: "Stay Connected",
    question: "Want early access to events before they go public?",
    hint: "Drop your email — you'll be first on the list"
  },
];

function Survey1() {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const total = s1Questions.length;
  const done = step === total;

  function setRadio(id, val) { setAnswers(a => ({ ...a, [id]: val })); }
  function toggleCheck(id, val) {
    setAnswers(a => {
      const cur = a[id] || [];
      return { ...a, [id]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
    });
  }
  function setScale(id, val) { setAnswers(a => ({ ...a, [id]: val })); }
  function setText(id, val) { setAnswers(a => ({ ...a, [id]: val })); }

  function canAdvance() {
    if (step < 0) return true;
    const q = s1Questions[step];
    if (!q.required) return true;
    const ans = answers[q.id];
    if (q.type === "radio") return !!ans;
    if (q.type === "scale") return !!ans;
    if (q.type === "check") return ans && ans.length > 0;
    return true;
  }

  async function handleNext() {
    if (step === total - 1) {
      const fields = { "form-name": "bodybymj-event-feedback" };
      Object.entries(answers).forEach(([k, v]) => {
        fields[k] = Array.isArray(v) ? v.join(", ") : String(v ?? "");
      });
      try {
        const res = await fetch("/", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: new URLSearchParams(fields).toString() });
        console.log("[Survey1] submit status:", res.status);
      } catch (e) {
        console.error("[Survey1] submit error:", e);
      }
    }
    setStep(s => s + 1);
  }

  if (done) return (
    <ThankYouScreen
      title="See you at the next one."
      body="Every answer goes directly into planning the next Body by MJ event. You're the reason we keep building."
      tag="Follow us @body.by.mj"
    />
  );

  if (step === -1) return (
    <WelcomeScreen
      badge="EVENT FEEDBACK SURVEY"
      title={"You showed up.\nNow help us\nlevel up."}
      subtitle="Your feedback shapes every event we build next."
      time="About 3 minutes"
      questionCount={total}
      cta="Let's go"
      onStart={() => setStep(0)}
    />
  );

  const q = s1Questions[step];

  return (
    <div>
      <ProgressBar current={step + 1} total={total} />
      <QuestionCounter current={step + 1} total={total} />
      {q.section && <SectionBadge label={q.section} />}
      {q.hint && <HintBadge text={q.hint} />}
      <h2 style={{
        fontFamily: "'Playfair Display'", fontSize: "clamp(20px,4vw,28px)",
        fontWeight: 700, color: "#2A5F70", lineHeight: 1.3, marginBottom: 24
      }}>{q.question}</h2>

      {q.type === "radio" && q.options.map(o => (
        <RadioOption key={o} label={o} selected={answers[q.id] === o} onSelect={() => setRadio(q.id, o)} />
      ))}
      {q.type === "check" && q.options.map(o => (
        <CheckOption key={o} label={o} selected={(answers[q.id] || []).includes(o)} onToggle={() => toggleCheck(q.id, o)} />
      ))}
      {q.type === "scale" && (
        <ScaleInput value={answers[q.id]} onChange={v => setScale(q.id, v)} low={q.low} high={q.high} max={q.max || 5} />
      )}
      {q.type === "text" && (
        <TextInput value={answers[q.id] || ""} onChange={v => setText(q.id, v)} placeholder={q.placeholder} />
      )}
      {q.type === "email" && (
        <EmailInput value={answers[q.id] || ""} onChange={v => setText(q.id, v)} />
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 32, justifyContent: "space-between" }}>
        <NavBtn onClick={() => setStep(s => s - 1)} label="← Back" disabled={step === 0} />
        <NavBtn onClick={handleNext} label={step === total - 1 ? "Submit ✓" : "Next →"} primary disabled={!canAdvance()} />
      </div>
    </div>
  );
}

// ── SURVEY 2: App Research ─────────────────────────────────────────────────

const s2Questions = [
  {
    id: "who_are_you", type: "radio", required: true,
    section: "About You",
    question: "What best describes you right now?",
    options: [
      "Current Body by MJ community member",
      "I use other fitness apps but not Body by MJ",
      "I'm new to fitness apps entirely",
      "I prefer in-person workouts and classes"
    ]
  },
  {
    id: "age", type: "radio", required: true,
    section: "About You",
    question: "How old are you?",
    options: ["18–24", "25–34", "35–44", "45–54", "55+"]
  },
  {
    id: "workout_location", type: "check", required: true,
    section: "About You",
    question: "Where do you primarily work out?",
    hint: "Select all that apply",
    options: [
      "At home — no equipment",
      "At home — with equipment",
      "At the gym",
      "Outdoors",
      "Studios / classes",
      "Mix of everything"
    ]
  },
  {
    id: "activity_level", type: "radio", required: true,
    section: "About You",
    question: "How active are you currently?",
    options: [
      "Just getting started — beginner",
      "I move sometimes — 1 to 2 times a week",
      "Fairly consistent — 3 to 4 times a week",
      "Very active — 5 or more times a week"
    ]
  },
  {
    id: "time_available", type: "radio", required: true,
    section: "About You",
    question: "How much time can you realistically dedicate to a workout?",
    options: [
      "Under 15 minutes",
      "15–30 minutes",
      "30–45 minutes",
      "45–60 minutes",
      "60+ minutes"
    ]
  },
  {
    id: "fitness_goals", type: "check", required: true,
    section: "Your Goals",
    question: "What are your primary fitness goals?",
    hint: "Select all that apply",
    options: [
      "Lose weight",
      "Build strength and muscle tone",
      "Improve flexibility and mobility",
      "Improve mental health and reduce stress",
      "Increase energy and stamina",
      "Postpartum recovery",
      "Overall health and longevity"
    ]
  },
  {
    id: "biggest_challenge", type: "check", required: true,
    section: "Your Goals",
    question: "What's your biggest challenge staying consistent?",
    hint: "Select all that apply",
    options: [
      "Not enough time in my day",
      "Losing motivation after a few weeks",
      "Not knowing what workout to do",
      "Lack of accountability",
      "Physical limitations or pain",
      "Life gets in the way — kids, work, stress"
    ]
  },
  {
    id: "barrier", type: "check", required: true,
    section: "Your Goals",
    question: "What are your biggest barriers to staying consistent with fitness?",
    hint: "Select all that apply — this tells us what problems we need to solve first",
    options: [
      "No time",
      "Don't know what's right for my body",
      "Can't afford a personal trainer",
      "I lose motivation fast",
      "My energy changes too much throughout the month",
      "I need accountability — I can't do it alone"
    ]
  },
  {
    id: "ai_value", type: "scale", required: true,
    section: "The App",
    question: "How valuable would AI-generated workouts tailored specifically to your body and goals be?",
    low: "Not useful at all", high: "I need this now", max: 5
  },
  {
    id: "ai_features", type: "check", required: true,
    section: "The App",
    question: "Which AI-powered features excite you most?",
    hint: "Select your top 3",
    options: [
      "A plan that adapts to my schedule and progress automatically",
      "Workouts synced to my menstrual cycle",
      "AI nutrition coaching and meal guidance",
      "Real-time form feedback using my camera",
      "A personal AI coach I can chat with anytime",
      "Recovery and sleep tracking integration",
      "Wearable device integration (Apple Watch, Fitbit, etc.)"
    ]
  },
  {
    id: "energy_shift", type: "scale", required: true,
    section: "Cycle Syncing",
    question: "How much does your energy shift throughout the month?",
    low: "Barely noticeable", high: "It runs my whole schedule", max: 5
  },
  {
    id: "cycle_sync", type: "radio", required: true,
    section: "Cycle Syncing",
    question: "Would you use a feature that adjusts your workouts and nutrition based on your cycle?",
    options: [
      "Yes — this is exactly what I need",
      "Yes — I'm curious to try it",
      "Maybe — I'd need to see how it works",
      "No — not something I track"
    ]
  },
  {
    id: "nutrition_support", type: "check", required: false,
    section: "Cycle Syncing",
    question: "What nutrition support would actually help you?",
    hint: "Select all that apply",
    options: [
      "Simple meal plans based on my goals",
      "Guidance tied to my cycle phase",
      "Grocery lists",
      "Hormone-supportive eating tips",
      "Just tell me what to eat — keep it simple"
    ]
  },
  {
    id: "community_importance", type: "scale", required: true,
    section: "Community",
    question: "How important is community to your fitness journey?",
    low: "Not important at all", high: "Absolutely essential", max: 5
  },
  {
    id: "community_features", type: "check", required: false,
    section: "Community",
    question: "What community features matter most to you?",
    hint: "Select all that apply",
    options: [
      "Accountability partner matching",
      "Group challenges and competitions",
      "Live workout classes with others",
      "Sharing progress and celebrating wins",
      "Private — I prefer to work out solo",
      "Forums and discussion spaces"
    ]
  },
  {
    id: "trainer_marketplace", type: "scale", required: true,
    section: "Community",
    question: "Would you use a marketplace to find and book personal trainers inside the app?",
    low: "Not interested", high: "I'd use it immediately", max: 5
  },
  {
    id: "mvp_features", type: "check", required: true,
    section: "Build Priority",
    question: "Which 3 features would make you download the app TODAY?",
    hint: "Select up to 3 — this is our MVP priority list",
    options: [
      "AI-generated personalized workouts",
      "Cycle syncing tracker and recommendations",
      "Nutrition plans tied to my goals",
      "Personal trainer marketplace",
      "Community and accountability groups",
      "Progress tracking and insights"
    ]
  },
  {
    id: "price", type: "radio", required: true,
    section: "Build Priority",
    question: "What would you pay per month for an app with your top picks?",
    options: [
      "Nothing — free only",
      "$5–$10 per month",
      "$10–$20 per month",
      "$20–$35 per month",
      "$35+ if it delivers real results"
    ]
  },

  {
    id: "missing", type: "text", required: false,
    section: "The Big Question",
    question: "What is missing from every fitness app you have ever tried?",
    hint: "Most important question in this survey — be completely honest",
    placeholder: "This shapes everything we build. Tell us exactly what's been missing for you..."
  },
  {
    id: "discovery", type: "radio", required: true,
    section: "About You",
    question: "How did you first hear about Body by MJ?",
    options: [
      "Instagram",
      "TikTok",
      "Sweatpals app",
      "A friend or family member",
      "In person / event",
      "Google",
      "Other"
    ]
  },
  {
    id: "waitlist_name", type: "text_single", required: false,
    section: "Join the Waitlist",
    question: "Want early access when the Body by MJ app launches?",
    hint: "Add your name and email below — founding members get exclusive early access and pricing",
    placeholder: "Your first name..."
  },
  {
    id: "waitlist_email", type: "email", required: false,
    section: "Join the Waitlist",
    question: "And your email address?",
    placeholder: "your@email.com"
  }
];

function Survey2() {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const total = s2Questions.length;
  const done = step === total;

  function setRadio(id, val) { setAnswers(a => ({ ...a, [id]: val })); }
  function toggleCheck(id, val) {
    setAnswers(a => {
      const cur = a[id] || [];
      const q = s2Questions.find(q => q.id === id);
      const isMax = q?.id === "mvp_features" && cur.length >= 3 && !cur.includes(val);
      if (isMax) return a;
      return { ...a, [id]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
    });
  }
  function setScale(id, val) { setAnswers(a => ({ ...a, [id]: val })); }
  function setText(id, val) { setAnswers(a => ({ ...a, [id]: val })); }

  function canAdvance() {
    if (step < 0) return true;
    const q = s2Questions[step];
    if (!q.required) return true;
    const ans = answers[q.id];
    if (q.type === "radio") return !!ans;
    if (q.type === "scale") return !!ans;
    if (q.type === "check") return ans && ans.length > 0;
    return true;
  }

  async function handleNext() {
    if (step === total - 1) {
      const fields = { "form-name": "bodybymj-app-research" };
      Object.entries(answers).forEach(([k, v]) => {
        fields[k] = Array.isArray(v) ? v.join(", ") : String(v ?? "");
      });
      try {
        const res = await fetch("/", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: new URLSearchParams(fields).toString() });
        console.log("[Survey2] submit status:", res.status);
      } catch (e) {
        console.error("[Survey2] submit error:", e);
      }
    }
    setStep(s => s + 1);
  }

  if (done) return (
    <ThankYouScreen
      color={TEAL}
      title="Your vision is now part of our mission."
      body="We're reading every single response. What you shared here directly shapes what we build next — stay close and watch this space."
      tag="Follow us @body.by.mj"
    />
  );

  if (step === -1) return (
    <WelcomeScreen
      color={TEAL}
      badge="YOUR VISION, OUR MISSION"
      title={"Your Vision.\nOur Mission.\nLet's build it\ntogether."}
      subtitle="This is where your voice becomes our blueprint. Tell us what you actually need — every answer shapes what we build."
      time="About 4 minutes"
      questionCount={total}
      cta="Share my vision"
      onStart={() => setStep(0)}
    />
  );

  const q = s2Questions[step];

  return (
    <div>
      <ProgressBar current={step + 1} total={total} color={TEAL} />
      <QuestionCounter current={step + 1} total={total} color={TEAL} />
      {q.section && <SectionBadge label={q.section} color={TEAL} />}
      {q.hint && <HintBadge text={q.hint} color={TEAL} />}
      <h2 style={{
        fontFamily: "'Playfair Display'", fontSize: "clamp(20px,4vw,28px)",
        fontWeight: 700, color: "#2A5F70", lineHeight: 1.3, marginBottom: 24
      }}>{q.question}</h2>

      {q.type === "radio" && q.options.map(o => (
        <RadioOption key={o} label={o} selected={answers[q.id] === o} onSelect={() => setRadio(q.id, o)} color={TEAL} />
      ))}
      {q.type === "check" && q.options.map(o => (
        <CheckOption key={o} label={o} selected={(answers[q.id] || []).includes(o)} onToggle={() => toggleCheck(q.id, o)} color={TEAL} />
      ))}
      {q.type === "scale" && (
        <ScaleInput value={answers[q.id]} onChange={v => setScale(q.id, v)} low={q.low} high={q.high} max={q.max || 5} color={TEAL} />
      )}
      {q.type === "text" && (
        <TextInput value={answers[q.id] || ""} onChange={v => setText(q.id, v)} placeholder={q.placeholder} />
      )}
      {q.type === "text_single" && (
        <TextInputSingle value={answers[q.id] || ""} onChange={v => setText(q.id, v)} placeholder={q.placeholder} color={TEAL} />
      )}
      {(q.type === "email" || q.id === "waitlist_email") && (
        <EmailInput value={answers[q.id] || ""} onChange={v => setText(q.id, v)} color={TEAL} />
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 32, justifyContent: "space-between" }}>
        <NavBtn onClick={() => setStep(s => s - 1)} label="← Back" color={TEAL} disabled={step === 0} />
        <NavBtn onClick={handleNext} label={step === total - 1 ? "Submit ✓" : "Next →"} primary color={TEAL} disabled={!canAdvance()} />
      </div>
    </div>
  );
}

// ── Page Shell (shared chrome, no tab switcher) ────────────────────────────

function PageShell({ children }) {
  return (
    <>
      <style>{globalStyle}</style>
      <div style={{ minHeight: "100vh", background: "#9FCBD9", display: "flex", flexDirection: "column", alignItems: "center", padding: "24px 16px 60px" }}>

        {/* Logo */}
        <Link to="/" style={{ textDecoration: "none", marginBottom: 32, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: "50%", background: PINK,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "'Playfair Display'", color: WHITE, fontWeight: 900, fontSize: 13
          }}>MJ</div>
          <span style={{ fontFamily: "'DM Sans'", fontWeight: 600, color: "#2A5F70", fontSize: 15, letterSpacing: "0.06em" }}>BODY BY MJ</span>
        </Link>

        {/* Survey card */}
        <div style={{
          width: "100%", maxWidth: 540,
          background: CARD, border: `1px solid ${BORDER}`,
          borderRadius: 20, padding: "36px 32px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.15)"
        }}>
          {children}
        </div>

        <p style={{ color: "#4A8BA0", fontFamily: "'DM Sans'", fontSize: 12, marginTop: 20 }}>
          Body by MJ · All responses are anonymous · bodysurvey.co
        </p>
      </div>
    </>
  );
}

// ── Landing Page ────────────────────────────────────────────────────────────

function DestinationCard({ to, color, eyebrow, title, subtitle, cta }) {
  return (
    <Link to={to} style={{
      display: "block", textDecoration: "none", textAlign: "left",
      background: `${color}10`, border: `1.5px solid ${color}40`,
      borderRadius: 16, padding: "24px 24px", marginBottom: 16,
      transition: "all 0.2s"
    }}>
      <div style={{
        display: "inline-block", color, fontFamily: "'DM Sans'",
        fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 10
      }}>{eyebrow}</div>
      <h2 style={{
        fontFamily: "'Playfair Display'", fontSize: 24, fontWeight: 900,
        color: "#2A5F70", lineHeight: 1.25, marginBottom: 8
      }}>{title}</h2>
      <p style={{ color: MUTED, fontFamily: "'DM Sans'", fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
        {subtitle}
      </p>
      <span style={{
        display: "inline-block", background: color, color: WHITE,
        borderRadius: 10, padding: "10px 22px", fontFamily: "'DM Sans'",
        fontSize: 14, fontWeight: 700
      }}>{cta} →</span>
    </Link>
  );
}

function Landing() {
  return (
    <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
      <h1 style={{
        fontFamily: "'Playfair Display'", fontSize: "clamp(24px,4.5vw,32px)",
        fontWeight: 900, color: "#2A5F70", lineHeight: 1.3, marginBottom: 8
      }}>Which one brought you here?</h1>
      <p style={{ color: MUTED, fontFamily: "'DM Sans'", fontSize: 14, marginBottom: 28 }}>
        Pick the one that fits — takes under 5 minutes either way.
      </p>
      <DestinationCard
        to="/events"
        color={PINK}
        eyebrow="EVENT FEEDBACK"
        title="Just left one of our events?"
        subtitle="Tell us how it went and what to build into the next one."
        cta="Give feedback"
      />
      <DestinationCard
        to="/app"
        color={TEAL}
        eyebrow="YOUR VISION, OUR MISSION"
        title="Want a say in the Body by MJ app?"
        subtitle="Help us design the app women actually need — your voice becomes our blueprint."
        cta="Share my vision"
      />
    </div>
  );
}

// ── App Shell (routing) ─────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PageShell><Landing /></PageShell>} />
        <Route path="/events" element={<PageShell><Survey1 /></PageShell>} />
        <Route path="/app" element={<PageShell><Survey2 /></PageShell>} />
      </Routes>
    </BrowserRouter>
  );
}
